import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Auth, confirmPasswordReset, verifyPasswordResetCode } from '@angular/fire/auth';
import { CapicheMaterialModule } from 'capiche-material';

@Component({
  selector: 'capiche-auth-reset-password',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, CapicheMaterialModule],
  template: `
    <div class="reset-page">
      <mat-card class="reset-card">
        <mat-card-header>
          <mat-card-title>{{ pageState === 'done' ? 'Password updated' : 'Choose a new password' }}</mat-card-title>
        </mat-card-header>

        <mat-card-content>

          <div *ngIf="pageState === 'invalid'" class="state-msg">
            <mat-icon class="icon-warn">error_outline</mat-icon>
            <p>This link is invalid or has already been used. Request a new one from the login page.</p>
            <a mat-stroked-button routerLink="/auth/login">Back to login</a>
          </div>

          <div *ngIf="pageState === 'done'" class="state-msg">
            <mat-icon class="icon-ok">check_circle_outline</mat-icon>
            <p>Your password has been updated. You can now sign in.</p>
            <a mat-raised-button color="primary" routerLink="/auth/login">Sign in</a>
          </div>

          <form *ngIf="pageState === 'ready'" (ngSubmit)="submit()" #f="ngForm">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>New password</mat-label>
              <input matInput [type]="showPwd ? 'text' : 'password'"
                [(ngModel)]="password" name="password" required minlength="8" [disabled]="loading">
              <mat-icon matPrefix>lock</mat-icon>
              <button mat-icon-button matSuffix type="button" (click)="showPwd = !showPwd">
                <mat-icon>{{ showPwd ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Confirm new password</mat-label>
              <input matInput [type]="showPwd ? 'text' : 'password'"
                [(ngModel)]="confirm" name="confirm" required [disabled]="loading">
              <mat-icon matPrefix>lock_outline</mat-icon>
            </mat-form-field>

            <p class="error-msg" *ngIf="errorMsg">{{ errorMsg }}</p>

            <button mat-raised-button color="primary" type="submit" class="full-width submit-btn"
              [disabled]="loading || !password || !confirm">
              <mat-spinner diameter="20" *ngIf="loading"></mat-spinner>
              <span *ngIf="!loading">Update password</span>
            </button>
          </form>

        </mat-card-content>
      </mat-card>
    </div>
  `,
  styles: [`
    :host { flex: 1; display: flex; flex-direction: column; }
    .reset-page { flex: 1; display: flex; align-items: center; justify-content: center; padding: 16px; }
    .reset-card { width: 100%; max-width: 440px; padding: 8px; }
    .full-width { width: 100%; }
    .submit-btn { height: 48px; font-size: 1rem; margin-top: 8px; }
    .error-msg { color: var(--capiche-warn, #C0392B); font-size: 0.9rem; margin: 4px 0; }
    .state-msg { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 16px 0; text-align: center; }
    .icon-ok { font-size: 48px; width: 48px; height: 48px; color: var(--capiche-teal, #40D1B7); }
    .icon-warn { font-size: 48px; width: 48px; height: 48px; color: var(--capiche-warn, #C0392B); }
  `],
})
export class ResetPasswordComponent implements OnInit {
  private auth = inject(Auth);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  pageState: 'verifying' | 'ready' | 'invalid' | 'done' = 'verifying';
  password = '';
  confirm = '';
  showPwd = false;
  loading = false;
  errorMsg = '';
  private oobCode = '';

  async ngOnInit() {
    this.oobCode = this.route.snapshot.queryParamMap.get('oobCode') ?? '';
    if (!this.oobCode) { this.pageState = 'invalid'; return; }
    try {
      await verifyPasswordResetCode(this.auth, this.oobCode);
      this.pageState = 'ready';
    } catch {
      this.pageState = 'invalid';
    }
  }

  async submit() {
    this.errorMsg = '';
    if (this.password.length < 8) { this.errorMsg = 'Password must be at least 8 characters.'; return; }
    if (this.password !== this.confirm) { this.errorMsg = 'Passwords do not match.'; return; }
    this.loading = true;
    try {
      await confirmPasswordReset(this.auth, this.oobCode, this.password);
      this.pageState = 'done';
    } catch {
      this.errorMsg = 'Failed to update password. The link may have expired — request a new one.';
    } finally {
      this.loading = false;
    }
  }
}
